'use client';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { cn } from '~/lib/cn';
import Badge from '../../Badge';
import { Button } from '../../Button';

import { easeInOut, motion } from 'framer-motion';

const containerVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.3,
            ease: easeInOut,
            staggerChildren: 0.1
        }
    }
};

export type ModuleVariant = 'Digital' | 'Hardware';

export interface Module {
    title: string;
    description: string;
    link?: string;
    icon: IconDefinition;
    variant: ModuleVariant;
    highlights: string[];
}

export interface ModulePresentationSectionProps {
    className?: string;
    isEven?: boolean;
    children?: React.ReactNode;
    module: Module;
}

export const ModulePresentationSection: React.FC<ModulePresentationSectionProps> = ({
    children,
    module,
    isEven = false,
    className,
    ...props
}) => {
    const shouldAlignImageLeft = !isEven;

    return (
        <motion.section
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ amount: 0.2, once: true }}
            className={cn('tw-grid tw-snap-start tw-snap-always tw-content-center tw-gap-12', className)}
            {...props}
        >
            {/* Header Section */}
            <header className="tw-space-y-6">
                <div className="tw-flex tw-items-center tw-gap-4">
                    <div
                        className="tw-card-bordered tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-2xl tw-text-primary"
                        aria-hidden="true"
                    >
                        <FontAwesomeIcon
                            icon={module.icon}
                            className="tw-h-7 tw-w-7"
                            aria-label={`${module.title} icon`}
                        />
                    </div>
                    <Badge flat>{module.variant}</Badge>
                </div>

                <h2
                    id={`module-title-${module.title.replace(/\s+/g, '-').toLowerCase()}`}
                    className="tw-text-3xl tw-font-bold tw-text-gray-900 md:tw-text-4xl lg:tw-text-5xl"
                >
                    {module.title}
                </h2>
            </header>

            <div className="tw-grid tw-items-center tw-gap-12 lg:tw-grid-cols-2 lg:tw-gap-16">
                <div className={cn('tw-space-y-8', shouldAlignImageLeft && 'lg:tw-order-2')}>
                    <div className="tw-space-y-6">
                        <p className="tw-text-lg tw-leading-relaxed tw-text-gray-600 md:tw-text-xl">
                            {module.description}
                        </p>

                        {module.highlights && module.highlights.length > 0 && (
                            <ul className="tw-space-y-3" role="list">
                                {module.highlights.map((highlight, index) => (
                                    <li key={index} className="tw-flex tw-items-start tw-gap-3">
                                        <span className="tw-mt-1.5 tw-h-2 tw-w-2 tw-flex-shrink-0 tw-rounded-full tw-bg-primary" />
                                        <span className="tw-text-gray-700">{highlight}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="tw-space-y-4">
                        {module.link ? (
                            <Button.Link
                                href={module.link}
                                size="md"
                                block
                                className="tw-group tw-items-center tw-gap-2"
                            >
                                Mehr Erfahren
                                <span className="tw-transition-transform tw-duration-200" aria-hidden="true">
                                    →
                                </span>
                            </Button.Link>
                        ) : (
                            <Button.Link href="/module" size="md">
                                Mehr Erfahren über weiteren Modulen
                            </Button.Link>
                        )}
                    </div>
                </div>

                <div className={cn('tw-relative', shouldAlignImageLeft && 'lg:tw-order-1')}>
                    <div
                        className="tw-relative tw-flex tw-aspect-[4/3] tw-items-center tw-justify-center tw-p-0"
                        aria-label={`Visual representation of ${module.title}`}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </motion.section>
    );
};
